import React, { useState, useEffect } from "react";
import {
    Input,
    Button,
    Checkbox,
    DatePicker,
    Slider,
    Radio,
    message,
    Modal,
} from "antd";
import { useParams } from "react-router-dom";
import { CameraOutlined } from "@ant-design/icons";
import styles from "./ShishkaGuestLogin.module.css";
import Confetti from "react-confetti";

const API_URL =
    "https://uvjwfdf5aa.execute-api.eu-west-1.amazonaws.com/test/setup-configuration-api";
const guestOnboardingAPI =
    "https://uvjwfdf5aa.execute-api.eu-west-1.amazonaws.com/test/guest-onboarding";
const guestLoginAPI =
    "https://uvjwfdf5aa.execute-api.eu-west-1.amazonaws.com/test/guest-login";

const ShishkaGuestLogin = () => {
    const { placeId } = useParams();
    const [backgroundImage, setBackgroundImage] = useState(null);
    const [hookahTypes, setHookahTypes] = useState([]);
    const [selectedHookahTypes, setSelectedHookahTypes] = useState([]);
    const [userName, setUserName] = useState("");
    const [userSurname, setUserSurname] = useState("");
    const [phone, setPhone] = useState("");
    const [dob, setDob] = useState(null);
    const [hookahPreferences, setHookahPreferences] = useState("");
    const [hookahDislikes, setHookahDislikes] = useState("");
    const [isChill, setIsChill] = useState(null);
    const [strengthRange, setStrengthRange] = useState([3, 4]);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [photo, setPhoto] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    useEffect(() => {
        fetchSetupConfig();
    }, [placeId]);

    const fetchSetupConfig = async () => {
        setLoading(true);
        try {
            const response = await fetch(API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ place_id: placeId, action: "read" }),
            });

            const data = await response.json();
            if (response.ok && data.data) {
                setHookahTypes(data.data.hookah_types || []);
                setBackgroundImage(data.presigned_url);
            } else {
                message.error("Ошибка загрузки данных");
            }
        } catch (error) {
            console.error("Ошибка:", error);
            message.error("Ошибка загрузки данных");
        } finally {
            setLoading(false);
        }
    };

    const handlePhotoCapture = () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        //input.capture = "user"; // Ensuring front camera usage

        input.onchange = (e) => {
            const file = e.target.files[0];
            if (!file) {
                message.error("Файл не выбран");
                return;
            }

            console.log("File selected:", file);

            const reader = new FileReader();
            reader.onload = () => {
                const photoDataUrl = reader.result;
                console.log("Captured Photo:", photoDataUrl);
                setPhoto(photoDataUrl);
            };
            reader.onerror = () => {
                console.error("Error reading file:", reader.error);
                message.error("Ошибка при чтении файла");
            };
            reader.readAsDataURL(file);
        };

        input.click();
    };

    const checkUserExists = async (photoDataUrl) => {
        setLoading(true);
        try {
            const response = await fetch(guestLoginAPI, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    place_id: placeId,
                    data_url: [photoDataUrl],
                }),
            });

            const data = await response.json();
            if (response.ok && data.body) {
                const userData = data.body;

                // Extract values from DynamoDB-style response
                const userName = userData.user_name?.S || "";
                const userSurname = userData.user_surname?.S || "";
                const phone = userData.user_mobile?.S || "";
                const dob = userData.user_date_of_birth?.S || null;
                const hookahPreferences =
                    userData.user_hookah_preferences?.SS?.join(",") || "";
                const hookahDislikes =
                    userData.user_hookah_dislikes?.SS?.join(",") || "";
                const isChill = userData.user_hookah_chill?.S === "True"; // Convert string to boolean
                const strengthRange =
                    userData.user_hookah_strength_range?.SS?.map(Number) || [
                        3, 4,
                    ];
                const selectedHookahTypes =
                    userData.user_hookah_types?.SS || [];

                // Pre-fill the form fields with existing user data
                setUserName(userName);
                setUserSurname(userSurname);
                setPhone(phone);
                setDob(dob);
                setHookahPreferences(hookahPreferences);
                setHookahDislikes(hookahDislikes);
                setIsChill(isChill);
                setStrengthRange(strengthRange);
                setSelectedHookahTypes(selectedHookahTypes);
                setAgreedToTerms(true); // Assuming the user has already agreed to terms

                message.success("Добро пожаловать обратно!");
            } else {
                message.info(
                    "Новый пользователь. Пожалуйста, заполните форму."
                );
            }
        } catch (error) {
            console.error("Ошибка:", error);
            message.error("Ошибка при проверке пользователя");
        } finally {
            setLoading(false);
        }
    };

    const isFormComplete =
        userName &&
        userSurname &&
        phone &&
        dob &&
        selectedHookahTypes.length > 0 &&
        agreedToTerms &&
        photo;

    const handleRegister = async () => {
        if (!photo) {
            message.error("Фото не загружено!");
            return;
        }
        setLoading(true);
        try {
            const requestBody = {
                place_id: placeId,
                properties: {
                    user_mobile: phone,
                    user_date_of_birth: dob,
                    user_hookah_preferences: hookahPreferences.split(","),
                    user_hookah_dislikes: hookahDislikes.split(","),
                    user_hookah_chill: isChill,
                    user_hookah_strength_range: strengthRange,
                    user_hookah_types: selectedHookahTypes,
                },
                user_name: userName,
                user_surname: userSurname,
                data_url: [photo],
            };

            const response = await fetch(guestOnboardingAPI, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requestBody),
            });

            const data = await response.json();
            if (response.ok) {
                setShowConfetti(true); // Show confetti
                setShowSuccessModal(true); // Show success modal
                message.success("Регистрация успешна!");
            } else {
                message.error(data.message || "Ошибка регистрации");
            }
        } catch (error) {
            console.error("Ошибка:", error);
            message.error("Ошибка регистрации");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
        setShowConfetti(false); // Stop confetti
    };

    return (
        <div className={styles.wrapper}>
            {backgroundImage && (
                <div
                    className={styles.background}
                    style={{ backgroundImage: `url(${backgroundImage})` }}
                />
            )}

            {/* Confetti Effect */}
            {showConfetti && (
                <Confetti
                    width={window.innerWidth}
                    height={window.innerHeight}
                    recycle={false}
                    numberOfPieces={500}
                    onConfettiComplete={() => setShowConfetti(false)}
                />
            )}

            {/* Success Modal */}
            <Modal
                visible={showSuccessModal}
                onCancel={handleCloseSuccessModal}
                footer={null}
                centered>
                <div style={{ textAlign: "center" }}>
                    <h2>Регистрация прошла успешно!</h2>
                    <p>Спасибо за регистрацию. Наслаждайтесь вашим временем!</p>
                    <Button type='primary' onClick={handleCloseSuccessModal}>
                        Закрыть
                    </Button>
                </div>
            </Modal>

            <div className={styles.formContainer}>
                <h1 className={styles.title}>Регистрация/логин</h1>

                <div className={styles.photoContainer}>
                    <div
                        className={styles.photoPreview}
                        onClick={handlePhotoCapture}
                        style={{
                            backgroundImage: `url(${backgroundImage})`, // Background image behind camera icon
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                        }}>
                        {photo ? (
                            <img
                                src={photo}
                                alt='Preview'
                                className={styles.photo}
                            />
                        ) : (
                            <CameraOutlined className={styles.cameraIcon} />
                        )}
                    </div>
                </div>

                <label className={styles.label}>Имя</label>
                <Input
                    className={styles.input}
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                />

                <label className={styles.label}>Фамилия</label>
                <Input
                    className={styles.input}
                    value={userSurname}
                    onChange={(e) => setUserSurname(e.target.value)}
                />

                <label className={styles.label}>Телефон</label>
                <Input
                    className={styles.input}
                    placeholder='+7'
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                />

                <label className={styles.label}>Дата рождения</label>
                <DatePicker
                    className={styles.datePicker}
                    format='DD.MM.YYYY'
                    placeholder='Дата рождения'
                    onChange={(date, dateString) => setDob(dateString)}
                />

                <h3 className={styles.sectionTitle}>Ваши предпочтения</h3>

                <label className={styles.label}>Вкусы, которые нравятся</label>
                <Input
                    className={styles.input}
                    placeholder='ягоды, фрукты'
                    value={hookahPreferences}
                    onChange={(e) => setHookahPreferences(e.target.value)}
                />

                <label className={styles.label}>
                    Вкусы, которые Не нравятся
                </label>
                <Input
                    className={styles.input}
                    placeholder='арбуз, дыня, апельсин'
                    value={hookahDislikes}
                    onChange={(e) => setHookahDislikes(e.target.value)}
                />

                <div className={styles.radioGroup}>
                    <Radio.Group
                        onChange={(e) => setIsChill(e.target.value)}
                        value={isChill}>
                        <Radio value={true}>С холодком</Radio>
                        <Radio value={false}>Без холодка</Radio>
                    </Radio.Group>
                </div>

                <label className={styles.label}>
                    Выберите крепость табака (1-10)
                </label>
                <Slider
                    range
                    min={1}
                    max={10}
                    defaultValue={[3, 4]}
                    onChange={(value) => setStrengthRange(value)}
                />

                <label className={styles.label}>
                    Предпочитаемые виды табака
                </label>
                <div className={styles.tobaccoOptions}>
                    {hookahTypes.map((type) => (
                        <Button
                            key={type}
                            className={`${styles.tobaccoButton} ${
                                selectedHookahTypes.includes(type)
                                    ? styles.selected
                                    : ""
                            }`}
                            onClick={() =>
                                setSelectedHookahTypes((prev) =>
                                    prev.includes(type)
                                        ? prev.filter((t) => t !== type)
                                        : [...prev, type]
                                )
                            }>
                            {type}
                        </Button>
                    ))}
                </div>

                <Checkbox
                    className={styles.checkbox}
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}>
                    Я согласен на обработку <a href='#'>данных</a>
                </Checkbox>

                <Button
                    type='primary'
                    className={`${styles.registerButton} ${
                        isFormComplete ? styles.active : ""
                    }`}
                    onClick={handleRegister}
                    disabled={!isFormComplete}>
                    Зарегистрироваться
                </Button>
            </div>
        </div>
    );
};

export default ShishkaGuestLogin;
